import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { login, logout } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import ieumLogo from '../../assets/ieum-logo.png';
import LoadingOverlay from '../../components/common/LoadingOverlay';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  const isDev = process.env.NODE_ENV === 'development';
  const [email, setEmail] = useState(isDev ? 'dididia@naver.com' : '');
  const [password, setPassword] = useState(isDev ? 'ieum1234!' : '');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (result.role !== 'admin' && result.role !== 'staff') {
        await logout();
        setError('관리자 계정이 아닙니다.');
        setLoading(false);
        return;
      }
      await refreshSession();
      navigate(result.role === 'staff' ? '/admin/mail/inbox' : '/admin', { replace: true });
    } catch (err) {
      const code = err.code || '';
      if (code === 'auth/too-many-requests') {
        setError('로그인 시도가 너무 많습니다. 잠시 후 다시 시도해주세요.');
      } else {
        setError('아이디가 존재하지 않거나 입력하신 정보를 다시 한번 확인해 주시기 바랍니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      {loading && <LoadingOverlay minimal message="로그인 중입니다..." />}
      <Card>
        <LogoArea>
          <LogoImg src={ieumLogo} alt="이음" />
          <LogoText>관리자</LogoText>
        </LogoArea>

        <form onSubmit={handleSubmit}>
          <Field>
            <Label>외부 이메일 또는 회사 이메일</Label>
            <Input
              type="email"
              placeholder="name@example.com 또는 name@ieum.in"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </Field>

          <Field>
            <Label>비밀번호</Label>
            <InputWrap>
              <Input
                type={showPw ? 'text' : 'password'}
                placeholder="비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                $hasIcon
                required
              />
              <EyeBtn type="button" onClick={() => setShowPw((v) => !v)}>
                {showPw ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
              </EyeBtn>
            </InputWrap>
          </Field>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </SubmitBtn>
        </form>
      </Card>
    </Wrapper>
  );
};

export default AdminLoginPage;

// ───── 스타일 ─────
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #F3F4F6;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 48px 40px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
`;

const LogoArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 40px;
`;

const LogoImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const LogoText = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #1F2937;
`;

const Field = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 6px;
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '44px' : '16px')};
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14px;
  color: #1F2937;
  outline: none;
  box-sizing: border-box;

  &::placeholder { color: #9CA3AF; }
  &:focus { border-color: #4A6CF7; }
`;

const EyeBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px;
`;

const ErrorMsg = styled.p`
  color: #EF4444;
  font-size: 13px;
  text-align: center;
  margin-bottom: 16px;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #4A6CF7;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { opacity: 0.9; }
`;
